import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';

import PropTypes from 'prop-types';

import { useCurrentProjectId } from '@adsk/alloy-provider';
import { Flex } from '@adsk/alloy-react-box';
import { useLocale } from '@adsk/alloy-react-locale';
import ProgressRing from '@adsk/alloy-react-progress-ring';

import CollectionList from './components/CollectionList';
import Table from './components/Table';
import { THUMBNAIL_PLACEHOLDER } from './helpers/consts';
import Services, { cancelRequests } from './helpers/sdkServices';
import { getDisplayCollections, getSheetsColumns } from './helpers/utils';
import { useCollection } from './providers/CollectionProvider';
import { useSheetsCommon } from './providers/CommonProvider';
import { useSearchPanel } from './providers/SearchPanelProvider';
import SheetCustomNoData from './SheetCustomNoData';
import { pluginShape } from '../../helpers/propTypes';
import ReferencePickerError from '../../ReferencePickerError';

const defaultPagination = {
  limit: 100,
  offset: 0,
  total: undefined,
  hasNext: undefined,
};

const SheetCustomDataView = ({
  disabledIds = [],
  selectedIds = [],
  filters,
  setFilters,
  openFiltersPanel,
  onChange,
  multiple,
  setSelection,
  plugin,
}) => {
  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
  const projectId = useCurrentProjectId();
  const { sheetParser, sheetContainerType } = useSheetsCommon();
  const [pagination, setPagination] = useState(defaultPagination);
  const [isLoading, setLoading] = useState(false);
  const [loadFailure, setLoadFailure] = useState(false);
  const [entities, setEntities] = useState([]);
  const [selectedEntities, setSelectedEntities] = useState([]);
  const [thumbnails, setThumbnails] = useState({});
  const thumbnailsRef = useRef(thumbnails);
  const searchText = filters.searchExpression;
  const {
    isFeatureEnabled: enableCollectionFeature,
    isEnabled: isCollectionEnabled,
    collections: collectionList,
    isLoading: isLoadingCollection,
    hasError: loadCollectionError,
    retry: getCollections,
    selectedId: selectedCollectionId,
    setSelectedCollectionId,
  } = useCollection();
  const { setTags: setPanelTags, resetSearchPanel } = useSearchPanel();
  const underSearchMode =
    !!filters.searchExpression ||
    !!filters.setId ||
    (!!filters.tags && filters.tags.length > 0);

  const { newSelectedIds, currentNewSelectedIds, currentSelectedIds } =
    useMemo(() => {
      const newSelectedIds = selectedIds.filter(
        (id) => !disabledIds.includes(id),
      );
      // new selected sheet ids under current collection
      const currentNewSelectedIds = newSelectedIds.filter((id) =>
        entities.find((entity) => entity.id === id),
      );
      // all selected ids in current collection, includes disabledIds
      const currentSelectedIds = selectedIds.filter((id) =>
        entities.find((entity) => entity.id === id),
      );

      return {
        newSelectedIds,
        currentNewSelectedIds,
        currentSelectedIds,
      };
    }, [selectedIds, disabledIds, entities]);

  // click clear button at footer
  useEffect(() => {
    if (newSelectedIds.length === 0) {
      setSelectedEntities([]);
    }
  }, [newSelectedIds.length]);

  const selectCountMap = useMemo(() => {
    const map = {};
    selectedEntities.forEach((entity) => {
      const id = entity.collection?.id;
      if (id) {
        map[id] = map[id] ? map[id] + 1 : 1;
      }
    });

    return map;
  }, [selectedEntities]);

  const collections = useMemo(
    () => getDisplayCollections(collectionList, selectCountMap),
    [collectionList, selectCountMap],
  );

  const showCollectionList = collections.length > 0 && !filters.searchAll;

  useEffect(() => {
    if (!isCollectionEnabled) return;
    if (!selectedCollectionId && collections[0]) {
      setSelectedCollectionId(collections[0].id);
    }
  }, [
    isCollectionEnabled,
    selectedCollectionId,
    collections,
    setSelectedCollectionId,
  ]);

  useEffect(() => {
    if (selectedCollectionId) setEntities([]);
  }, [selectedCollectionId]);

  const getSheets = useCallback(
    async ({
      offset = defaultPagination.offset,
      limit = defaultPagination.limit,
    } = {}) => {
      // Get sheets with default selected collectionId
      // If collection disabled or no display collection,
      // query sheets under project or default collection without collectionId
      if (
        isCollectionEnabled &&
        collections.length > 0 &&
        !selectedCollectionId
      ) {
        return;
      }

      setLoading(true);
      setLoadFailure(false);

      try {
        if (Services.Sheets.loadSheetsCancelToken) {
          cancelRequests(Services.Sheets.loadSheetsCancelToken);
        }

        const queryParams = {
          sheetContainerType,
          offset,
          limit,
          searchText: filters.searchExpression,
          ...(filters.setId && { 'filter[setId]': filters.setId }),
          ...((filters.tags || []).length > 0 && {
            'filter[tags]': filters.tags,
          }),
          ...(filters.withAllTags && { withAllTags: true }),
          ...(selectedCollectionId && {
            collectionId: filters.searchAll ? '*' : selectedCollectionId,
          }),
        };

        const { sheets = [], pagination: page = {} } =
          await Services.Sheets.getSheets(projectId, queryParams);

        const list = sheetParser ? sheets.map(sheetParser) : sheets;
        const { totalResults: total } = page;

        // clear entities when `page` refreshed
        if (offset === 0) {
          setEntities([...list]);
        } else {
          setEntities((entities) => [...entities, ...list]);
        }
        setPagination({
          limit,
          offset: offset + (limit > total ? total : limit),
          total,
          hasNext: total > limit + offset,
        });
      } catch (err) {
        //  skip cancel request error
        if (!err.__CANCEL__) {
          setLoadFailure(true);
        }
      } finally {
        setLoading(false);
      }
    },
    [
      isCollectionEnabled,
      collections.length,
      sheetContainerType,
      filters.searchExpression,
      filters.setId,
      filters.tags,
      filters.withAllTags,
      filters.searchAll,
      selectedCollectionId,
      projectId,
      sheetParser,
    ],
  );

  useEffect(() => {
    if (isLoadingCollection || loadCollectionError) return;
    setPagination(defaultPagination);
    getSheets();
  }, [
    isLoadingCollection,
    loadCollectionError,
    isCollectionEnabled,
    selectedCollectionId,
    filters,
    getSheets,
  ]);

  useEffect(() => {
    thumbnailsRef.current = thumbnails;
  }, [thumbnails]);

  useEffect(() => {
    return () => {
      Object.values(thumbnailsRef.current).forEach(
        (blob) =>
          blob && blob !== THUMBNAIL_PLACEHOLDER && URL.revokeObjectURL(blob),
      );
    };
  }, []);

  const handleSelectCollection = useCallback(
    (collectionId) => {
      setSelectedCollectionId(collectionId);
      setFilters({});
      setPagination(defaultPagination);
      if (underSearchMode) resetSearchPanel();
    },
    [setSelectedCollectionId, setFilters, underSearchMode, resetSearchPanel],
  );

  const onTagClick = useCallback(
    (tag) => {
      if (enableCollectionFeature) {
        setPanelTags((tags) => {
          if (!tags || tags.length === 0) return [tag];
          return tags.includes(tag) ? [...tags] : [...tags, tag];
        });
      } else {
        if ((filters.tags || []).includes(tag)) return;
        setFilters({ ...filters, tags: (filters.tags || []).concat(tag) });
      }
      openFiltersPanel();
    },
    [
      enableCollectionFeature,
      setPanelTags,
      filters,
      setFilters,
      openFiltersPanel,
    ],
  );

  // for toggle one row selection
  const handleChange = useCallback(
    (target) => {
      const { entity, checked } = target;
      setSelectedEntities((selectedEntities) =>
        !checked
          ? multiple
            ? selectedEntities.filter(({ id }) => id !== entity.id)
            : []
          : multiple
            ? [...selectedEntities, entity]
            : [entity],
      );
      onChange(target);
    },
    [multiple, onChange, setSelectedEntities],
  );

  // for select all selection
  const handleSetSelection = useCallback(
    (entities) => {
      let totalSelected = [];
      // todo: May need handle selection orders in future
      // remove all current selected in current collection, excludes disabled
      if (entities.length === 0) {
        totalSelected = selectedEntities.filter(
          ({ id }) =>
            !currentNewSelectedIds.includes(id) && !disabledIds.includes(id),
        );
      } else {
        // remove duplicated ids, excludes disabled
        totalSelected = [...new Set([...entities, ...selectedEntities])].filter(
          ({ id }) => !disabledIds.includes(id),
        );
      }

      // for single mode, just at most one can be selected
      if (!multiple) {
        if (entities[0]?.id === selectedIds[0]) {
          totalSelected = [];
        } else {
          totalSelected = entities;
        }
      }
      setSelectedEntities(totalSelected);
      setSelection(totalSelected);
    },
    [
      multiple,
      setSelection,
      selectedIds,
      currentNewSelectedIds,
      disabledIds,
      selectedEntities,
    ],
  );

  const columns = useMemo(
    () =>
      getSheetsColumns({
        T,
        disabledIds,
        thumbnails,
        searchText,
        filterTags: filters.tags,
        searchAll: filters.searchAll,
        setThumbnails,
        onTagClick,
        onCollectionClick: handleSelectCollection,
      }),
    [
      T,
      thumbnails,
      searchText,
      filters.tags,
      filters.searchAll,
      disabledIds,
      setThumbnails,
      onTagClick,
      handleSelectCollection,
    ],
  );

  const renderSheets = () => {
    const isLoadingSheets = isLoading && pagination.offset === 0;
    const isLoadingMoreSheets = isLoading && pagination.offset > 0;
    const loadSheetsFailure = loadFailure && pagination.offset === 0;
    const loadMoreSheetsFailure = loadFailure && pagination.offset > 0;

    if (isLoadingSheets) return <ProgressRing size="medium" />;

    if (loadSheetsFailure) {
      return <ReferencePickerError onTryAgain={getSheets} />;
    }

    if (entities.length === 0) {
      return (
        <SheetCustomNoData
          filters={filters}
          clearFilters={() => setFilters({})}
          plugin={plugin}
        />
      );
    }

    return (
      <Table
        multiple={multiple}
        columns={columns}
        entities={entities}
        disabledIds={disabledIds}
        selectedIds={currentSelectedIds}
        loading={isLoadingMoreSheets}
        error={loadMoreSheetsFailure}
        onLoadMore={() =>
          pagination.hasNext &&
          getSheets({ offset: pagination.offset, limit: pagination.limit })
        }
        onTryAgain={() =>
          getSheets({ offset: pagination.offset, limit: pagination.limit })
        }
        onChange={handleChange}
        setSelection={handleSetSelection}
      />
    );
  };

  if (isLoadingCollection) {
    return (
      <Flex justifyContent="center" padding="24px">
        <ProgressRing size="medium" />
      </Flex>
    );
  }

  if (loadCollectionError) {
    return <ReferencePickerError onTryAgain={getCollections} />;
  }

  return (
    <div
      style={{
        display: 'flex',
        flexShrink: 0,
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {showCollectionList && (
        <CollectionList
          list={collections}
          selectedId={selectedCollectionId}
          onClick={handleSelectCollection}
        />
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 'auto',
          overflow: 'hidden',
        }}
      >
        {renderSheets()}
      </div>
    </div>
  );
};

SheetCustomDataView.propTypes = {
  disabledIds: PropTypes.array,
  selectedIds: PropTypes.array,
  multiple: PropTypes.bool,
  onChange: PropTypes.func,
  filters: PropTypes.shape({
    searchExpression: PropTypes.string,
    setId: PropTypes.string,
    tags: PropTypes.array,
    searchAll: PropTypes.bool,
    withAllTags: PropTypes.bool,
  }),
  setFilters: PropTypes.func,
  openFiltersPanel: PropTypes.func,
  setSelection: PropTypes.func,
  plugin: pluginShape.isRequired,
};

export default SheetCustomDataView;
